import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { isNil, defaultTo, isNull, forEach, noop } from 'lodash';
import { stringifyUrl } from 'query-string';

const isCsr = typeof window !== 'undefined';
const nativeLazySupported =
    isCsr &&
    'HTMLImageElement' in window &&
    'loading' in HTMLImageElement.prototype;

if (isCsr && !nativeLazySupported) {
    import('lazysizes');
    import('lazysizes/plugins/parent-fit/ls.parent-fit');
}

const Image = ({ src, alt, priority, classNames, sizes, callbacks }) => {
    const url = src || '';
    const [HTMLPictureSupport, setHTMLPictureSupport] = useState(true);

    const setImgRef = useCallback((imgEl) => {
        if (!imgEl) {
            return null;
        }
        if (callbacks?.onLoaded) {
            setTimeout(() => {
                const isCached = !!imgEl.currentSrc;
                let img = isCached ? document.createElement('img') : imgEl;

                const onLoad = () => {
                    callbacks.onLoaded(imgEl);
                    destroy();
                };

                function destroy() {
                    img.removeEventListener('load', onLoad);
                    img = null;
                }

                const onError = () => {
                    (callbacks.onFailed || noop)();
                    destroy();
                };

                img.addEventListener('load', onLoad);
                img.addEventListener('error', onError);
                if (isCached) {
                    img.src = imgEl.currentSrc;
                }
            });
        }
        if (!nativeLazySupported) {
            return null;
        }
        forEach(imgEl.parentNode.querySelectorAll('source'), (el) => {
            el.setAttribute('srcset', el.getAttribute('data-srcset'));
        });
        imgEl.setAttribute('src', imgEl.getAttribute('data-src'));
        return null;
    }, []);

    useEffect(() => {
        if (!window.HTMLPictureElement) {
            setHTMLPictureSupport(false);
        }
    }, []);

    const mediaUrlPrefix = /\/([-~]{1})\/media\//i;
    const match = mediaUrlPrefix.exec(url);
    const newUrl = isNull(match)
        ? url
        : url.replace(mediaUrlPrefix, `/${match[1]}/jssmedia/`);
    const composeUrl = (w, h) =>
        stringifyUrl({
            url: newUrl,
            query: {
                w,
                h,
            },
        });

    const helmetContent = priority ? (
        <Helmet>
            {sizes.map(({ w, h, media }, index) => {
                const helmetKey = `image__${media}__${index}`;

                return (
                    <link
                        key={helmetKey}
                        rel="preload"
                        fetchPriority="high"
                        href={composeUrl(w, h)}
                        as="image"
                        media={media}
                    />
                );
            })}
        </Helmet>
    ) : null;

    const useFallback = !priority && !nativeLazySupported;

    // Inline styles have been added to make sure the area doesn't shift before
    // and after the image is loaded. It is also used to make sure that the img
    // tag has a defined width and height, which is another of the requirement
    // from Lighthouse.
    return (
        <>
            {helmetContent}
            {HTMLPictureSupport ? (
                <picture
                    className={`Image ${defaultTo(classNames.picture, '')}`}>
                    {sizes
                        .filter(({ w, h }) => !isNil(w) || !isNil(h))
                        .map(({ w, h, media }, index) => {
                            const pictureKey = `picture__${media}__${index}`;

                            return (
                                <source
                                    key={pictureKey}
                                    data-srcset={composeUrl(w, h)}
                                    srcSet={
                                        priority ? composeUrl(w, h) : undefined
                                    }
                                    media={media}
                                />
                            );
                        })}
                    <img
                        ref={setImgRef}
                        alt={alt}
                        data-src={newUrl}
                        fetchPriority={priority ? 'high' : undefined}
                        src={
                            priority
                                ? newUrl
                                : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
                        }
                        className={`Image__img ${
                            useFallback ? 'lazyload' : ''
                        } ${defaultTo(classNames.img, '')}`}
                        loading={!priority ? 'lazy' : 'eager'}
                    />
                </picture>
            ) : (
                <div className={`Image ${defaultTo(classNames.picture, '')}`}>
                    <img
                        ref={setImgRef}
                        alt={alt}
                        data-src={newUrl}
                        fetchPriority={priority ? 'high' : undefined}
                        src={
                            priority
                                ? newUrl
                                : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
                        }
                        className={`Image__img ${
                            useFallback ? 'lazyload' : ''
                        } ${defaultTo(classNames.img, '')}`}
                        loading={!priority ? 'lazy' : 'eager'}
                    />
                </div>
            )}
        </>
    );
};

Image.propTypes = {
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    priority: PropTypes.bool,
    classNames: PropTypes.shape({
        picture: PropTypes.string,
        img: PropTypes.string,
    }),
    sizes: PropTypes.arrayOf(
        PropTypes.shape({
            w: PropTypes.number,
            h: PropTypes.number,
            media: PropTypes.string,
        })
    ),
};

Image.defaultProps = {
    priority: false,
    classNames: {
        picture: '',
        img: '',
    },
    sizes: [],
};

export default Image;
